import type { SxStyle, SxStyleFunc } from '@aibees/shared/theme';

export const tableContainer: SxStyle = {
  // TableContainer defaults to `overflow-x: auto`, which makes it a scroll
  // container and breaks `position: sticky` on the header cells (they would
  // pin relative to this never-scrolling container instead of the page).
  overflow: 'visible',
};

export const table: SxStyle = {
  backgroundColor: (p) => p.palette.common.white,
  border: '1px solid',
  borderColor: 'divider',
  // 8px on all corners to match the other page sections. The bottom-most row's
  // cells drop their own bottom border (see CollapsibleSection's `isLast`) so
  // the table's rounded bottom border is the single bottom edge — without that
  // the two borders would stack into a doubled "thick" line under the last
  // section.
  borderRadius: 1,
  // With `border-collapse: separate` the sticky header cells keep square
  // corners, so round the top corner cells to match the rounded frame. The
  // bottom corner cells are transparent, so the table's own border rounds them.
  '& thead th:first-of-type': { borderTopLeftRadius: '8px' },
  '& thead th:last-of-type': { borderTopRightRadius: '8px' },
  // The compact PlanCard fills the last header cell edge-to-edge with its own
  // (white / most-popular) background, so round its top-right corner too —
  // otherwise its square corner covers the frame's rounded corner.
  '& thead th:last-of-type .PlanCard': { borderTopRightRadius: '8px' },
  // `border-collapse: collapse` (MUI default) makes `box-shadow` unreliable on
  // sticky `<td>` cells. `separate` plus zero spacing keeps the look identical
  // but lets each cell render its own shadow cleanly.
  borderCollapse: 'separate',
  borderSpacing: 0,
};

const stickyHeaderShadow = {
  content: '""',
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: -6,
  height: 6,
  pointerEvents: 'none',
  background:
    'linear-gradient(180deg, rgba(15, 23, 42, 0.16), rgba(15, 23, 42, 0))',
};

export const headerFeatureCell: SxStyle = {
  verticalAlign: 'middle',
  borderRight: '1px solid',
  borderBottom: '1px solid',
  borderColor: 'divider',
  minWidth: (p) => p.spacing(30),
  p: 3,
  height: '1px',
  position: 'sticky',
  top: 0,
  zIndex: 2,
  bgcolor: 'grey.200',
  '&::after': stickyHeaderShadow,
};

export const divider: SxStyle = {
  mb: 1.5,
};

export const comparePlansCell: SxStyle = {};

export const headerPlanCell: SxStyle = {
  verticalAlign: 'top',
  borderRight: '1px solid',
  borderBottom: '1px solid',
  borderColor: 'divider',
  p: 0,
  width: 269,
  minWidth: 269,
  '&:last-of-type': { borderRight: 0 },
  position: 'sticky',
  top: 0,
  zIndex: 2,
  bgcolor: 'background.paper',
  '&::after': stickyHeaderShadow,
};

export const featureRow: SxStyle = {};

// When a flat row is the terminal row of the table (possible now that rows
// render in server compareOrder, not two fixed blocks), drop its cells' bottom
// border so it doesn't double up with the table frame's rounded bottom edge —
// mirrors CollapsibleSection's `atBottom` for the section branch.
export const lastFlatRow: SxStyle = {
  '& > td': { borderBottom: 0 },
};

// `centered` stacks the feature name and its "Powered by …" badge as a
// centered pair (used for powered-by rows); other rows stay left-aligned.
export const topRowFeatureCell: SxStyleFunc<boolean> = (centered) => ({
  pl: 5,
  pr: 1,
  py: 1.5,
  borderRight: '1px solid',
  borderBottom: '1px solid',
  borderColor: 'divider',
  minWidth: (p) => p.spacing(35),
  ...(centered && { textAlign: 'center' }),
});

export const featureValueCell: SxStyle = {
  borderRight: '1px solid',
  borderBottom: '1px solid',
  borderColor: 'divider',
  width: 269,
  minWidth: 269,
  '&:last-of-type': { borderRight: 0 },
};
